import * as R from 'remeda';
import { useQuery } from '@tanstack/react-query';
import { Location } from 'history';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { replace } from 'redux-first-history';
import { endpoints, getIDFromUrl } from '@rossum/api-client';
import {
  AccessibleChildAnnotation,
  StartEditPagesResponse,
} from '@rossum/api-client/annotations';
import { HttpError } from '@rossum/api-client/errors';
import { EditRelation } from '@rossum/api-client/relations';
import { api } from '../../lib/apiClient';
import { constructDocumentUrl } from '../../lib/url';
import { SuggestedEdit } from '../../redux/modules/annotation/types';
import { getAnnotationBacklink } from '../../routes/useAnnotationBacklink';
import { Annotation, AnnotationSideloads } from '../../types/annotation';
import { QueueSettings } from '../../types/queue';
import { notify } from '../toaster';
import { EditDocumentConfig, EditDocumentMode, Part } from './editState';

/**
 * `useStartEditMode` reads only a handful of fields off the annotation, its
 * sideloads and the organization. Declaring those subsets explicitly lets both
 * the redux page (`document-edit`) and the react-query one (`document-split`)
 * pass their own, slightly different, shapes without casting.
 */
export type StartEditModeAnnotation = Pick<
  Annotation,
  'id' | 'url' | 'queue' | 'status' | 'suggestedEdit'
>;

export type StartEditModeSideloads = {
  document: { originalFileName?: string | null } | undefined;
  relations: AnnotationSideloads['relations'];
  queue: { settings?: QueueSettings } | undefined;
};

export type StartEditModeOrganization = {
  uiSettings?: {
    features?: {
      disable_splitting?: boolean;
      prefer_in_place_splitting?: boolean;
    };
  };
};

export const determineEditDocumentMode = (
  annotation: StartEditModeAnnotation,
  annotationSideloads: StartEditModeSideloads,
  startEditResponse: StartEditPagesResponse,
  location: Location
): Promise<EditDocumentMode> => {
  // NOTE: Relation of type "edit" is not attached to parent annotation,
  // so we can't use it here.
  if (annotation.status === 'split') {
    const fromAnnotationId = Number(
      new URLSearchParams(location.search).get('from')
    );

    const validChildAnnotation = startEditResponse.children.find(
      (a): a is AccessibleChildAnnotation =>
        'url' in a && a.url !== null && getIDFromUrl(a.url) === fromAnnotationId
    );

    return Promise.resolve({
      type: 'parent-annotation',
      fromAnnotation: validChildAnnotation?.url,
    });
  }

  const edit = annotationSideloads.relations.find(
    (r): r is EditRelation => r.type === 'edit'
  );

  if (!edit) return Promise.resolve({ type: 'single-annotation' });

  return api
    .request(endpoints.annotations.get(getIDFromUrl(edit.parent)))
    .then(() => ({
      type: 'child-annotation' as const,
      parentIsAccessible: true,
      parentUrl: edit.parent,
    }))
    .catch(e => {
      if (e instanceof HttpError && e.code === 404) {
        return {
          type: 'child-annotation',
          parentIsAccessible: false,
          parentUrl: edit.parent,
        };
      }
      // TODO what to do here?
      throw e;
    });
};

type UseStartEditModeParams = {
  annotation: StartEditModeAnnotation | undefined;
  annotationSideloads: StartEditModeSideloads;
  organization: StartEditModeOrganization | undefined;
};

export const useStartEditMode = ({
  annotation,
  annotationSideloads,
  organization,
}: UseStartEditModeParams) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // string is URL - it will be replaced with SuggestedEdit object
  const waitingForSuggestedEdit =
    typeof annotation?.suggestedEdit === 'string' &&
    annotation?.status !== 'split';

  // From Redux, we need annotation, organization, and suggested edits to be loaded
  const enabled = !!annotation && !waitingForSuggestedEdit && !!organization;

  const location = useLocation();

  return useQuery({
    queryKey: ['start-edit', annotation?.id] as const,

    queryFn: async () => {
      if (!annotation)
        return Promise.reject(
          new Error('Cannot start edit mode without annotation.')
        );

      if (!organization)
        return Promise.reject(
          new Error('Cannot start edit mode without organization.')
        );

      const startEditResponse = await api.request(
        endpoints.annotations.startEditPages(annotation.id)
      );

      const mode = await determineEditDocumentMode(
        annotation,
        annotationSideloads,
        startEditResponse,
        location
      );

      const suggestedEdit =
        annotation.suggestedEdit && typeof annotation.suggestedEdit !== 'string'
          ? annotation.suggestedEdit
          : null;

      return buildEditDocumentConfiguration(
        startEditResponse,
        annotation,
        annotationSideloads,
        organization,
        suggestedEdit,
        mode
      );
    },
    enabled,
    cacheTime: 0,
    onError: e => {
      if (e instanceof HttpError && e.code === 409) {
        // The user has no indication that a parent document is locked by somebody else,
        // so we have to explain it in the error message.
        notify.error({
          title: intl.formatMessage({
            id: 'containers.message.editModeConflictError',
          }),
        });
      } else {
        notify.error({
          title: intl.formatMessage({ id: 'containers.message.clientError' }),
        });
      }

      const validationScreenEnabled =
        !!annotationSideloads.queue?.settings?.uiValidationScreenEnabled;

      // User can access edit mode directly even for annotations where it can't be accessed,
      // so we just redirect them back to validation screen.
      if (annotation) {
        if (validationScreenEnabled) {
          dispatch(replace(constructDocumentUrl({ id: annotation.id })));
        } else {
          dispatch(replace(getAnnotationBacklink()));
        }
      }
    },
    retry: false,
  });
};

export const buildEditDocumentConfiguration = async (
  response: StartEditPagesResponse,
  annotation: StartEditModeAnnotation,
  annotationSideloads: StartEditModeSideloads,
  organization: StartEditModeOrganization,
  suggestedEdit: SuggestedEdit | null,
  mode: EditDocumentMode
): Promise<EditDocumentConfig> => {
  const pagesData = await api.unpaginatedRequest(endpoints.pages.list)({
    annotation: annotation.id,
    ordering: 'number',
  });

  const pageUrlToNumber = Object.fromEntries(
    pagesData.map(p => [p.url, p.number] as const)
  );

  const pageUrlToRotation = Object.fromEntries(
    pagesData.map(p => [p.url, p.rotationDeg] as const)
  );

  // Map page URL to page number, throwing an error if the page is not found
  const getPageNumber = (url: string): number => {
    const pageNumber = pageUrlToNumber[url];
    if (pageNumber === undefined) {
      throw new Error(`Unknown page received: ${url}.`);
    }
    return pageNumber;
  };

  const getPageRotation = (url: string): number => {
    return pageUrlToRotation[url] ?? 0;
  };

  const settings = {
    disableSplitting:
      organization.uiSettings?.features?.disable_splitting ?? false,
    preferInPlaceSplitting:
      organization.uiSettings?.features?.prefer_in_place_splitting ?? false,
  };

  const queueSettings = annotationSideloads.queue?.settings;
  const editValueDefaults = Object.fromEntries(
    (queueSettings?.uiEditValues ?? []).map(ev => [ev.id, ''])
  );

  const createEditValues = (values?: Record<string, string>) => ({
    ...editValueDefaults,
    ...values,
  });

  // Step 3: Build the configuration
  // single annotation
  if (response.children.length === 0) {
    const initialParts: Part[] = [
      {
        annotationType: 'accessible',
        annotation: {
          status: annotation.status,
          filename: annotationSideloads.document?.originalFileName ?? 'N/A',
          queueUrl: annotation.queue,
          started: true,
          url: annotation.url,
        },
        pages: R.pipe(
          pagesData,
          R.sortBy(p => p.number),
          R.map(p => ({
            url: p.url,
            pageNumber: p.number,
            deleted: false,
            initialDeleted: false,
            rotationDeg: p.rotationDeg,
            initialRotationDeg: p.rotationDeg,
          }))
        ),
        targetQueueUrl: annotation.queue,
        editValues: createEditValues(),
      },
    ];

    const initialPartsWithSuggestedSplits = suggestedEdit
      ? suggestedEdit.documents.map(
          ({ pages: docPages, targetQueue, values }) => ({
            pages: R.pipe(
              docPages,
              R.map(p => ({
                url: p.page,
                pageNumber: getPageNumber(p.page),
                deleted: p.deleted ?? false,
                initialDeleted: p.deleted ?? false,
                rotationDeg: p.rotationDeg ?? getPageRotation(p.page),
                initialRotationDeg: p.rotationDeg ?? getPageRotation(p.page),
              }))
            ),
            annotationType: 'none' as const,
            targetQueueUrl: targetQueue ?? annotation.queue,
            editValues: createEditValues(values),
          })
        )
      : null;

    return {
      initialParts,
      initialPartsWithSuggestedSplits,
      mode,
      annotationUrl: annotation.url,
      settings,
    };
  }

  // parent annotation
  const partsForChildAnnotations: Part[] = response.children.map(
    childAnnotation => {
      const childPages = childAnnotation.parentPages.map(p => ({
        url: p.page,
        pageNumber: getPageNumber(p.page),
        rotationDeg: p.rotationDeg,
        initialRotationDeg: p.rotationDeg,
        deleted: p.deleted ?? false,
        initialDeleted: p.deleted ?? false,
      }));

      if ('url' in childAnnotation && childAnnotation.url !== null) {
        return {
          annotationType: 'accessible',
          annotation: {
            status: childAnnotation.status,
            filename: childAnnotation.originalFileName,
            queueUrl: childAnnotation.queue,
            started: childAnnotation.started,
            url: childAnnotation.url,
          },
          targetQueueUrl: childAnnotation.queue,
          pages: childPages,
          editValues: createEditValues(childAnnotation.values),
        };
      }

      const hasAnyVisiblePage = childPages.some(p => !p.deleted);

      if (!hasAnyVisiblePage) {
        const values =
          'values' in childAnnotation ? childAnnotation.values : undefined;

        return {
          annotationType: 'none',
          pages: childPages,
          targetQueueUrl: annotation.queue,
          editValues: createEditValues(values),
        };
      }

      return {
        annotationType: 'inaccessible',
        annotation: {
          started: false,
          filename: childAnnotation.originalFileName,
        },
        pages: childPages,
      };
    }
  );

  return {
    initialParts: partsForChildAnnotations,
    // We don't show suggested splits when displaying parent annotation
    initialPartsWithSuggestedSplits: null,
    mode,
    annotationUrl: annotation.url,
    settings,
  };
};
